'use client';

import type { Dayjs } from 'dayjs';
import type { Theme } from '@mui/material/styles';

import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/material/styles';

import { varAlpha } from 'src/theme/styles';

import { datum, avstånd, HORISONT_DAGAR, placeraPåTidslinjen } from './valkomstlage';

import type { Tidslinjepost, Tidslinjegrupp } from './valkomstlage';

// ----------------------------------------------------------------------

/** Inom en vecka byter markeringen till varningsfärg. */
const SNART_DAGAR = 7;

/** Minsta avstånd mellan två datumetiketter, som andel av remsans bredd. */
const ETIKETTAVSTÅND = 0.1;

/** Det mobila läget visar de närmaste posterna som lista i stället för remsa. */
const MOBILA_RADER = 3;

const linjenRitas = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const markeringenTänds = keyframes`
  from { opacity: 0; transform: translate(-50%, -50%) scale(0.4); }
  to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

/** Remsans enda rörelse: linjen ritas och förfallodagarna tänds i tidsordning. */
const utanRörelse = { '@media (prefers-reduced-motion: reduce)': { animation: 'none' } };

// ----------------------------------------------------------------------

type Props = {
  poster: Tidslinjepost[];
  idag: Dayjs;
  onÖppna: (post: Tidslinjepost) => void;
  /** Sant i hubben: varje post visar vilken förening den gäller. */
  visaOrganisation?: boolean;
};

export function Tidslinje({ poster, idag, onÖppna, visaOrganisation }: Props) {
  const { försenade, grupper, senare } = placeraPåTidslinjen(poster, idag);

  return (
    <>
      <Remsa
        försenade={försenade}
        grupper={grupper}
        senare={senare}
        idag={idag}
        onÖppna={onÖppna}
        visaOrganisation={visaOrganisation}
      />
      <MobilLista poster={poster} idag={idag} onÖppna={onÖppna} visaOrganisation={visaOrganisation} />
    </>
  );
}

// ----------------------------------------------------------------------

type RemsProps = Omit<Props, 'poster'> & ReturnType<typeof placeraPåTidslinjen>;

function Remsa({ försenade, grupper, senare, idag, onÖppna, visaOrganisation }: RemsProps) {
  // Datumetiketterna glesas ut så att de inte skriver över varandra. Den närmaste har alltid sin.
  const medEtikett = new Set<number>();
  let senast = -Infinity;
  grupper.forEach((grupp, index) => {
    if (index === 0 || grupp.andel - senast >= ETIKETTAVSTÅND) {
      medEtikett.add(grupp.dagarKvar);
      senast = grupp.andel;
    }
  });

  const närmast = grupper[0];

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{ display: { xs: 'none', sm: 'flex' }, mt: 3 }}
    >
      {försenade.length > 0 && (
        <Försenat poster={försenade} idag={idag} onÖppna={onÖppna} visaOrganisation={visaOrganisation} />
      )}

      <Axeletikett>idag</Axeletikett>

      <Box sx={{ position: 'relative', flex: '1 1 auto', height: 64 }}>
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '50%',
            height: 2,
            mt: '-1px',
            borderRadius: 1,
            bgcolor: 'divider',
            transformOrigin: 'left center',
            animation: `${linjenRitas} 420ms cubic-bezier(0.2, 0, 0, 1) both`,
            ...utanRörelse,
          }}
        />

        {närmast && <Närmastetikett grupp={närmast} visaOrganisation={visaOrganisation} />}

        <Box
          component="ol"
          aria-label={`Förfallodagar de närmaste ${HORISONT_DAGAR} dagarna`}
          sx={{ m: 0, p: 0, listStyle: 'none' }}
        >
          {grupper.map((grupp, index) => (
            <Markering
              key={grupp.dagarKvar}
              grupp={grupp}
              ordning={index}
              idag={idag}
              visaDatum={medEtikett.has(grupp.dagarKvar)}
              onÖppna={onÖppna}
              visaOrganisation={visaOrganisation}
            />
          ))}
        </Box>
      </Box>

      <Axeletikett>
        {HORISONT_DAGAR} dagar
        {senare > 0 && (
          <Box component="span" sx={{ display: 'block', color: 'text.disabled' }}>
            +{senare} senare
          </Box>
        )}
      </Axeletikett>
    </Stack>
  );
}

function Axeletikett({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        flexShrink: 0,
        color: 'text.secondary',
        fontFamily: (theme) => theme.typography.fontSecondaryFamily,
        fontWeight: 600,
        lineHeight: 1.3,
        textAlign: 'center',
      }}
    >
      {children}
    </Typography>
  );
}

// ----------------------------------------------------------------------

function färgFör(dagarKvar: number) {
  return dagarKvar <= SNART_DAGAR ? 'warning' : 'primary';
}

/** Etiketten ankras mot markeringen men får aldrig gå utanför remsan. */
function ankare(andel: number) {
  if (andel < 0.06) return { left: 0, transform: 'none', textAlign: 'left' as const };
  if (andel > 0.94) return { right: 0, transform: 'none', textAlign: 'right' as const };
  return { left: `${andel * 100}%`, transform: 'translateX(-50%)', textAlign: 'center' as const };
}

/** Rubriken ovanför den närmaste markeringen — remsans enda permanenta text. */
function Närmastetikett({ grupp, visaOrganisation }: { grupp: Tidslinjegrupp; visaOrganisation?: boolean }) {
  const [första] = grupp.poster;
  const fler = grupp.poster.length - 1;
  // Långt till höger växer etiketten åt vänster, så att den inte trycks ut ur remsan.
  const position =
    grupp.andel > 0.6
      ? { right: `${(1 - grupp.andel) * 100}%`, transform: 'translateX(12px)', textAlign: 'right' as const }
      : ankare(grupp.andel);

  return (
    <Typography
      aria-hidden
      variant="subtitle2"
      noWrap
      sx={{ position: 'absolute', top: 0, maxWidth: '55%', ...position }}
    >
      {första.titel}
      {visaOrganisation && första.orgNamn && (
        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
          {' '}
          i {första.orgNamn}
        </Box>
      )}
      {fler > 0 && (
        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
          {' '}
          och {fler} till
        </Box>
      )}
    </Typography>
  );
}

type MarkeringProps = {
  grupp: Tidslinjegrupp;
  ordning: number;
  idag: Dayjs;
  visaDatum: boolean;
  onÖppna: (post: Tidslinjepost) => void;
  visaOrganisation?: boolean;
};

function Markering({ grupp, ordning, idag, visaDatum, onÖppna, visaOrganisation }: MarkeringProps) {
  const färg = färgFör(grupp.dagarKvar);
  const när = `${datum(grupp.dag, idag)}, ${avstånd(grupp.dag, idag)}`;
  const titlar = grupp.poster.map((post) =>
    visaOrganisation && post.orgNamn ? `${post.titel} (${post.orgNamn})` : post.titel
  );
  const [första] = grupp.poster;

  return (
    <Box component="li">
      <Tooltip
        arrow
        title={
          <Box sx={{ py: 0.25 }}>
            {titlar.map((titel) => (
              <Box key={titel}>{titel}</Box>
            ))}
            <Box sx={{ mt: 0.5, opacity: 0.72 }}>{när}</Box>
          </Box>
        }
      >
        <ButtonBase
          aria-label={`${titlar.join(', ')}. ${när}`}
          onClick={() => onÖppna(första)}
          sx={(theme: Theme) => ({
            position: 'absolute',
            top: '50%',
            left: `${grupp.andel * 100}%`,
            // Klickytan är större än pricken; pricken ritas av ::after.
            width: 28,
            height: 28,
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            animation: `${markeringenTänds} 260ms ${180 + ordning * 45}ms cubic-bezier(0.2, 0, 0, 1) both`,
            ...utanRörelse,
            '&::after': {
              content: '""',
              width: grupp.poster.length > 1 ? 14 : 12,
              height: grupp.poster.length > 1 ? 14 : 12,
              borderRadius: '50%',
              bgcolor: `${färg}.main`,
              boxShadow: `0 0 0 3px ${theme.vars.palette.background.paper}`,
              transition: theme.transitions.create('transform', { duration: 120 }),
            },
            '&:hover::after, &.Mui-focusVisible::after': { transform: 'scale(1.35)' },
            '&.Mui-focusVisible': {
              outline: `2px solid ${theme.vars.palette[färg].main}`,
              outlineOffset: 1,
            },
          })}
        />
      </Tooltip>

      {visaDatum && (
        <Typography
          aria-hidden
          variant="caption"
          noWrap
          sx={{
            position: 'absolute',
            bottom: 0,
            color: färg === 'warning' ? 'warning.dark' : 'text.secondary',
            fontFamily: (theme) => theme.typography.fontSecondaryFamily,
            fontWeight: 600,
            ...ankare(grupp.andel),
          }}
        >
          {grupp.dag.locale('sv').format('D MMM')}
        </Typography>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------------

function Försenat({
  poster,
  idag,
  onÖppna,
  visaOrganisation,
}: {
  poster: Tidslinjepost[];
  idag: Dayjs;
  onÖppna: (post: Tidslinjepost) => void;
  visaOrganisation?: boolean;
}) {
  const [äldst] = poster;
  const etikett = poster.length === 1 ? '1 försenad' : `${poster.length} försenade`;
  const äldstText = `${äldst.titel}${visaOrganisation && äldst.orgNamn ? ` (${äldst.orgNamn})` : ''}`;

  return (
    <Tooltip arrow title={`Äldst: ${äldstText}, förföll ${datum(äldst.förfallodag, idag)}`}>
      <ButtonBase
        onClick={() => onÖppna(äldst)}
        aria-label={`${etikett}. Äldst: ${äldstText}, förföll ${datum(äldst.förfallodag, idag)}`}
        sx={(theme: Theme) => ({
          flexShrink: 0,
          px: 1.5,
          py: 0.75,
          borderRadius: 1,
          color: 'error.dark',
          bgcolor: varAlpha(theme.vars.palette.error.mainChannel, 0.1),
          fontFamily: theme.typography.fontSecondaryFamily,
          fontWeight: 700,
          fontSize: theme.typography.pxToRem(14),
          '&:hover': { bgcolor: varAlpha(theme.vars.palette.error.mainChannel, 0.16) },
          '&.Mui-focusVisible': { outline: `2px solid ${theme.vars.palette.error.main}` },
          ...theme.applyStyles('dark', { color: 'error.light' }),
        })}
      >
        {etikett}
      </ButtonBase>
    </Tooltip>
  );
}

// ----------------------------------------------------------------------

/** Under `sm` finns ingen bredd för en remsa. De närmaste posterna blir en kort lista. */
function MobilLista({ poster, idag, onÖppna, visaOrganisation }: Props) {
  if (poster.length === 0) return null;
  const visade = poster.slice(0, MOBILA_RADER);

  return (
    <Box
      component="ol"
      aria-label="Närmaste förfallodagar"
      sx={{ display: { xs: 'block', sm: 'none' }, m: 0, mt: 2.5, p: 0, listStyle: 'none' }}
    >
      {visade.map((post) => {
        const dagar = post.förfallodag.startOf('day').diff(idag.startOf('day'), 'day');
        const färg = dagar < 0 ? 'error.main' : dagar <= SNART_DAGAR ? 'warning.dark' : 'text.secondary';
        return (
          <Box component="li" key={post.id}>
            <ButtonBase
              onClick={() => onÖppna(post)}
              sx={{
                width: 1,
                py: 1,
                gap: 2,
                justifyContent: 'flex-start',
                textAlign: 'left',
                borderTop: (theme) => `1px dashed ${theme.vars.palette.divider}`,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  width: 56,
                  flexShrink: 0,
                  color: färg,
                  fontFamily: (theme) => theme.typography.fontSecondaryFamily,
                }}
              >
                {post.förfallodag.locale('sv').format('D MMM')}
              </Typography>
              <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                {post.titel}
                {visaOrganisation && post.orgNamn && (
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {' '}
                    i {post.orgNamn}
                  </Box>
                )}
              </Typography>
            </ButtonBase>
          </Box>
        );
      })}
    </Box>
  );
}
